/**
 * 공통 스타일 적용 헬퍼 함수
 * options.style에 정의된 폰트/배경 스타일을 차트 옵션에 적용
 */

/**
 * 폰트 스타일 객체를 ECharts textStyle 형식으로 변환
 */
export const convertToTextStyle = (fontStyle) => {
  if (!fontStyle) return {};
  
  return {
    fontFamily: fontStyle.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: fontStyle.fontSize || 14,
    fontWeight: fontStyle.fontWeight || 'normal',
    fontStyle: fontStyle.fontStyle || 'normal',
    color: fontStyle.color || '#333333',
    lineHeight: fontStyle.lineHeight
  };
};

/**
 * 제목 스타일 적용
 */
export const applyTitleStyle = (titleOption, style) => {
  if (!style?.fonts?.title) return titleOption;
  
  return {
    ...titleOption,
    textStyle: {
      ...titleOption.textStyle,
      ...convertToTextStyle(style.fonts.title)
    }
  };
};

/**
 * 범례 스타일 적용
 */
export const applyLegendStyle = (legendOption, style) => {
  if (!style?.fonts?.legend) return legendOption;
  
  return {
    ...legendOption,
    textStyle: {
      ...legendOption.textStyle,
      ...convertToTextStyle(style.fonts.legend)
    }
  };
};

/**
 * X축 스타일 적용
 */
export const applyXAxisStyle = (xAxisOption, style) => {
  if (!style?.fonts?.axis?.x) return xAxisOption;
  
  const updatedAxis = { ...xAxisOption };
  
  // X축 제목 스타일
  if (style.fonts.axis.x.title) {
    updatedAxis.nameTextStyle = {
      ...updatedAxis.nameTextStyle,
      ...convertToTextStyle(style.fonts.axis.x.title)
    };
  }
  
  // X축 라벨 스타일
  if (style.fonts.axis.x.label) {
    updatedAxis.axisLabel = {
      ...updatedAxis.axisLabel,
      ...convertToTextStyle(style.fonts.axis.x.label)
    };
  }
  
  return updatedAxis;
};

/**
 * Y축 스타일 적용
 */
export const applyYAxisStyle = (yAxisOption, style) => {
  if (!style?.fonts?.axis?.y) return yAxisOption;
  
  // Y축이 배열인 경우 (이중 Y축)
  if (Array.isArray(yAxisOption)) {
    return yAxisOption.map(axis => applySingleYAxisStyle(axis, style));
  }
  
  // 단일 Y축
  return applySingleYAxisStyle(yAxisOption, style);
};

const applySingleYAxisStyle = (axis, style) => {
  const updatedAxis = { ...axis };
  
  // Y축 제목 스타일
  if (style.fonts.axis.y.title) {
    updatedAxis.nameTextStyle = {
      ...updatedAxis.nameTextStyle,
      ...convertToTextStyle(style.fonts.axis.y.title)
    };
  }
  
  // Y축 라벨 스타일
  if (style.fonts.axis.y.label) {
    const labelStyle = convertToTextStyle(style.fonts.axis.y.label);
    updatedAxis.axisLabel = {
      ...updatedAxis.axisLabel,
      ...labelStyle,
      // formatter는 유지
      formatter: updatedAxis.axisLabel?.formatter
    };
  }
  
  return updatedAxis;
};

/**
 * 시리즈 라벨 스타일 적용
 */
export const applySeriesLabelStyle = (seriesOption, style) => {
  if (!style?.fonts?.seriesLabel) return seriesOption;
  
  const labelStyle = convertToTextStyle(style.fonts.seriesLabel);
  
  return seriesOption.map(series => ({
    ...series,
    label: {
      ...series.label,
      ...labelStyle,
      // formatter와 show는 유지
      formatter: series.label?.formatter,
      show: series.label?.show
    },
    // emphasis 라벨에도 적용
    emphasis: series.emphasis ? {
      ...series.emphasis,
      label: {
        ...series.emphasis.label,
        ...labelStyle,
        formatter: series.emphasis.label?.formatter,
        show: series.emphasis.label?.show
      }
    } : undefined
  }));
};

/**
 * 배지/게이지의 값 스타일 적용
 */
export const applyValueStyle = (valueOption, style) => {
  if (!style?.fonts?.value) return valueOption;
  
  return {
    ...valueOption,
    ...convertToTextStyle(style.fonts.value)
  };
};

/**
 * 배지/게이지의 단위 스타일 적용
 */
export const applyUnitStyle = (unitOption, style) => {
  if (!style?.fonts?.unit) return unitOption;
  
  return {
    ...unitOption,
    ...convertToTextStyle(style.fonts.unit)
  };
};

/**
 * 레이더 차트 축 스타일 적용
 */
export const applyRadarAxisStyle = (radarOption, style) => {
  if (!radarOption || !style?.fonts?.axis) return radarOption;
  
  const updatedRadar = { ...radarOption };
  
  // angleAxis (각도축) - X축 스타일을 적용
  if (style.fonts.axis.x) {
    // 축 제목 스타일 (indicator 이름)
    if (style.fonts.axis.x.title) {
      updatedRadar.axisName = {
        ...updatedRadar.axisName,
        ...convertToTextStyle(style.fonts.axis.x.title)
      };
    }
    
    // 축 라벨 스타일 (각도축 값)
    if (style.fonts.axis.x.label) {
      updatedRadar.axisLabel = {
        ...updatedRadar.axisLabel,
        ...convertToTextStyle(style.fonts.axis.x.label)
      };
    }
  }
  
  // radiusAxis (반경축) - Y축 스타일을 적용
  if (style.fonts.axis.y) {
    // 반경축은 일반적으로 axisLabel에 표시됨
    if (style.fonts.axis.y.label) {
      // 레이더차트의 splitNumber 라벨에 적용
      const labelStyle = convertToTextStyle(style.fonts.axis.y.label);
      updatedRadar.axisLabel = {
        ...updatedRadar.axisLabel,
        ...labelStyle
      };
    }
  }
  
  return updatedRadar;
};

/**
 * 전체 차트 옵션에 공통 스타일 적용
 */
export const applyCommonStyles = (chartOption, style) => {
  if (!style || Object.keys(style).length === 0) return chartOption;
  
  const updatedOption = { ...chartOption };
  
  // 제목 표시/숨김 및 스타일 적용
  if (updatedOption.title) {
    // visibility 설정 확인
    if (style?.visibility?.title === false) {
      updatedOption.title.show = false;
    } else {
      updatedOption.title = applyTitleStyle(updatedOption.title, style);
    }
  }
  
  // 범례 표시/숨김 처리
  if (!updatedOption.legend) {
    updatedOption.legend = {};
  }
  
  // 하위 호환성: 기존 options.showLegend이 있고 visibility.legend이 없는 경우 마이그레이션
  let legendVisibility = true;
  if (style?.visibility?.legend !== undefined) {
    legendVisibility = style.visibility.legend !== false;
  } else if (style?.options?.showLegend !== undefined) {
    // 기존 설정이 있으면 그것을 사용 (마이그레이션 대상)
    legendVisibility = style.options.showLegend;
  }
  
  updatedOption.legend.show = legendVisibility;
  
  // 범례 스타일 적용 (표시 상태일 때만)
  if (updatedOption.legend && updatedOption.legend.show !== false) {
    updatedOption.legend = applyLegendStyle(updatedOption.legend, style);
  }
  
  // 레이더차트인 경우 radar 설정에 스타일 적용
  if (updatedOption.radar) {
    updatedOption.radar = applyRadarAxisStyle(updatedOption.radar, style);
  } else {
    // 일반 차트의 경우 X/Y축 스타일 적용
    if (updatedOption.xAxis) {
      // X축 제목 표시/숨김
      if (style?.visibility?.axis?.x?.title === false) {
        updatedOption.xAxis.name = '';
      }
      updatedOption.xAxis = applyXAxisStyle(updatedOption.xAxis, style);
    }
    
    if (updatedOption.yAxis) {
      // Y축이 배열인 경우 (이중 Y축)
      if (Array.isArray(updatedOption.yAxis)) {
        updatedOption.yAxis = updatedOption.yAxis.map(axis => {
          // Y축 제목 표시/숨김
          if (style?.visibility?.axis?.y?.title === false) {
            axis.name = '';
          }
          return axis;
        });
      } else {
        // 단일 Y축 제목 표시/숨김
        if (style?.visibility?.axis?.y?.title === false) {
          updatedOption.yAxis.name = '';
        }
      }
      updatedOption.yAxis = applyYAxisStyle(updatedOption.yAxis, style);
    }
  }
  
  // 시리즈 라벨 표시/숨김 및 스타일 적용
  if (updatedOption.series) {
    // 데이터 라벨 표시/숨김
    if (style?.visibility?.seriesLabel === false) {
      updatedOption.series = updatedOption.series.map(series => ({
        ...series,
        label: {
          ...series.label,
          show: false
        },
        emphasis: series.emphasis ? {
          ...series.emphasis,
          label: {
            ...series.emphasis.label,
            show: false
          }
        } : undefined
      }));
    } else {
      updatedOption.series = applySeriesLabelStyle(updatedOption.series, style);
    }
  }
  
  return updatedOption;
};